
import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Package, ShoppingBag } from "lucide-react";
import { OrdersManagement } from "@/components/seller/orders/OrdersManagement";
import { OrderStatus } from "@/utils/orderStatus";
import { useToast } from "@/hooks/use-toast";
import { useIsMobile } from "@/hooks/use-mobile";
import { createLogger } from "@/utils/logger";

const logger = createLogger('SellerOrders');

// Define the type for order items
interface OrderItem {
  id: string;
  meal_id: string;
  quantity: number;
  price: number;
  subtotal: number;
  meal: {
    name: string;
    image?: string;
  };
}

// Define the type for the order data we expect from the database
interface OrderData {
  id: string;
  status: OrderStatus;
  quantity: number;
  total_amount: number;
  scheduled_date: string | null;
  scheduled_time: string;
  delivery_type: "pickup" | "delivery";
  delivery_address: string | null;
  pickup_window_start: string | null;
  pickup_window_end: string | null;
  special_instructions: string | null;
  created_at: string;
  ready_at: string | null;
  ready_by: string | null;
  foodie: {
    first_name: string;
    last_name: string;
  };
  meal: {
    id: string;
    name: string;
    price: number;
    image: string | null;
  };
  order_items?: OrderItem[]; // Add order items array
}

const SellerOrders = () => {
  const { toast } = useToast();
  const isMobile = useIsMobile();

  const {
    data: orders,
    isLoading,
    refetch
  } = useQuery({
    queryKey: ["seller-orders"],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      // First, get all orders
      const { data: ordersData, error: ordersError } = await supabase
        .from("orders")
        .select(`
          id,
          status,
          quantity,
          total_amount,
          scheduled_date,
          scheduled_time,
          delivery_type,
          delivery_address,
          pickup_window_start,
          pickup_window_end,
          special_instructions,
          created_at,
          ready_at,
          ready_by,
          foodie:profiles!orders_foodie_id_fkey (
            first_name,
            last_name
          ),
          meal:meals (
            id,
            name,
            price,
            image
          )
        `)
        .eq("chef_id", user.id)
        .order("created_at", { ascending: false });

      if (ordersError) {
        logger.error("Error fetching orders:", ordersError);
        throw ordersError;
      }
      
      // Next, get order items for all orders in a single query
      const orderIds = ordersData.map(order => order.id);
      
      if (orderIds.length === 0) {
        return [];
      }
      
      const { data: orderItemsData, error: itemsError } = await supabase
        .from("order_items")
        .select(`
          id,
          order_id,
          meal_id,
          quantity,
          price,
          subtotal,
          meal:meals (
            id, 
            name,
            image
          )
        `)
        .in("order_id", orderIds);
        
      if (itemsError) {
        logger.error("Error fetching order items:", itemsError);
        // Continue with orders even if items fail
      } else if (orderItemsData && orderItemsData.length > 0) {
        logger.info(`Found ${orderItemsData.length} order items for ${orderIds.length} orders`);
        
        // Group items by order_id
        const itemsByOrder = orderItemsData.reduce((acc, item) => {
          if (!acc[item.order_id]) {
            acc[item.order_id] = [];
          }
          acc[item.order_id].push(item);
          return acc;
        }, {} as Record<string, OrderItem[]>);
        
        // Add items to their respective orders
        ordersData.forEach(order => {
          if (itemsByOrder[order.id] && itemsByOrder[order.id].length > 0) {
            // @ts-ignore: Add order_items to the order
            order.order_items = itemsByOrder[order.id];
          }
        });
      }
      
      // Validate and transform the data to ensure it matches our OrderData type
      const typedData = (ordersData || []) as unknown as OrderData[];
      return typedData;
    },
  });

  // Handle order status change
  const handleStatusChange = async (orderId: string, newStatus: OrderStatus) => {
    try {
      await refetch();
    } catch (error: any) {
      console.error("Error refreshing orders:", error);
      toast({
        title: "Error",
        description: "Failed to refresh orders after status change",
        variant: "destructive",
      });
    }
  };

  // Filter out incomplete orders for the counts
  const filteredOrders = orders ? orders.filter(order => order.status !== 'incomplete') : [];

  const currentOrdersCount = filteredOrders.filter(order => 
    ["in_progress", "ready"].includes(order.status)
  ).length || 0;
  
  const completedOrdersCount = filteredOrders.filter(order => 
    order.status === "completed"
  ).length || 0;

  return (
    <div className="container mx-auto py-4 md:py-6 px-4 md:px-6 space-y-4">
      <div className="flex justify-between items-center mb-2">
        <h1 className="text-xl md:text-2xl font-bold">Orders</h1>
        
        <div className="flex items-center text-sm space-x-4">
          <div className="flex items-center">
            <ShoppingBag className="h-4 w-4 mr-1 text-blue-500" />
            <span>Active: </span>
            <span className="font-semibold ml-1">{currentOrdersCount}</span>
          </div>
          
          <div className="flex items-center">
            <Package className="h-4 w-4 mr-1 text-green-500" />
            <span>Completed: </span>
            <span className="font-semibold ml-1">{completedOrdersCount}</span>
          </div>
        </div>
      </div>

      <OrdersManagement 
        orders={orders || []}
        onStatusChange={handleStatusChange}
        isLoading={isLoading}
      />
    </div>
  );
};

export default SellerOrders;
